import { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  AlertTriangle
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import { api } from '@/shared/api/endpoints';
import { useAuthStore } from '@/shared/stores/authStore';
import type { DepositData } from '@/shared/types';


export default function DepositManagement() {
  const [deposits, setDeposits] = useState<DepositData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('completed');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [selectedDeposit, setSelectedDeposit] = useState<DepositData | null>(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { user: authStoreUser, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || authStoreUser?.role == "user") {
    window.location.href = '/login';
    return null;
  }


  const depositsPerPage = 20;

  useEffect(() => {
    fetchDeposits();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchDeposits = async () => {
    try {
      const params = {
        page: currentPage.toString(),
        limit: depositsPerPage.toString(),
        search: searchTerm,
        status: statusFilter
      };

      const response = await api.admin.getDeposits(params);
      if (response.success) {
        setDeposits(response.data?.deposits || []);
        setTotalDeposits(response.data?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDepositAction = async (depositId: string, action: string) => {
    setActionLoading(depositId);

    try {
      const response = await api.admin.updateDepositStatus(depositId.toString(), { action });

      if (response.success) {
        fetchDeposits();
        setShowDepositModal(false);
      }
    } catch (error) {
      console.error(`Error ${action} deposit:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const exportDeposits = async () => {
    try {
      const blob = await api.admin.exportDeposits();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `deposits_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting deposits:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
    };

    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const totalPages = Math.ceil(totalDeposits / depositsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading deposits...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Deposit Management</h1>
            <p className="text-gray-600 mt-1">Review and process user deposits</p>
          </div>
          <button
            onClick={exportDeposits}
            className="bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user email, reference, or amount..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>

              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
            <span>Showing {deposits.length} of {totalDeposits} deposits</span>
            <span>Page {currentPage} of {totalPages}</span>
          </div>
        </div>

        {/* Deposits Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">User</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Package</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Payment</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deposits.map((deposit) => (
                  <tr key={deposit.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {deposit.user_name || 'Unnamed User'}
                        </div>
                        <div className="text-sm text-gray-500">{deposit.user_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-lg text-gray-900">
                        {formatCurrency(deposit.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {deposit.package_name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 capitalize">
                          {deposit.payment_method || 'Unknown'}
                        </div>
                        {deposit.payment_reference && (
                          <div className="text-gray-500 text-xs font-mono">
                            {deposit.payment_reference}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(deposit.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(deposit.status)}`}>
                          {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(deposit.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(deposit.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedDeposit(deposit);
                            setShowDepositModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-700 p-1 rounded transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {deposit.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleDepositAction(deposit.id, 'completed')}
                              disabled={actionLoading === deposit.id}
                              className="text-green-600 hover:text-green-700 p-1 rounded transition-colors disabled:opacity-50"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDepositAction(deposit.id, 'rejected')}
                              disabled={actionLoading === deposit.id}
                              className="text-red-600 hover:text-red-700 p-1 rounded transition-colors disabled:opacity-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex space-x-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + Math.max(1, currentPage - 2);
                    if (page > totalPages) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === page
                          ? 'bg-red-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Deposit Details Modal */}
        {showDepositModal && selectedDeposit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Deposit Details</h3>
                  <button
                    onClick={() => setShowDepositModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User
                    </label>
                    <div className="text-gray-900 font-medium">{selectedDeposit.user_name}</div>
                    <div className="text-gray-500 text-sm">{selectedDeposit.user_email}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(selectedDeposit.amount)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package
                    </label>
                    <div className="text-gray-900">{selectedDeposit.package_name}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method
                    </label>
                    <div className="text-gray-900 capitalize">
                      {selectedDeposit.payment_method || 'Not specified'}
                    </div>
                  </div>
                </div>

                {selectedDeposit.payment_reference && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Reference
                    </label>
                    <div className="bg-gray-100 rounded-lg p-3 font-mono text-sm">
                      {selectedDeposit.payment_reference}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(selectedDeposit.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedDeposit.status)}`}>
                        {selectedDeposit.status.charAt(0).toUpperCase() + selectedDeposit.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Created
                    </label>
                    <div className="text-gray-900">
                      {new Date(selectedDeposit.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {selectedDeposit.status === 'pending' && (
                  <div className="flex space-x-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleDepositAction(selectedDeposit.id, 'completed')}
                      disabled={actionLoading === selectedDeposit.id}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Approve Deposit</span>
                    </button>
                    <button
                      onClick={() => handleDepositAction(selectedDeposit.id, 'rejected')}
                      disabled={actionLoading === selectedDeposit.id}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      <XCircle className="h-4 w-4" />
                      <span>Reject Deposit</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
